/* eslint-disable no-underscore-dangle, no-console, react/destructuring-assignment, no-nested-ternary */
import React, { PureComponent } from 'react';
import deepmerge from 'deepmerge';
import deepEqual from 'deep-equal';
import ApplicationContext from '../context/context';
import {
  ShellProps,
  ShellState,
  ConfigurationManifestComponent
} from '../typings';
import log from '../helpers/log';
import loadGlobals from '../loader/load-files/load-files-globals';
import getFilteredFiles from '../helpers/get-filtered-files';
import loadFilesAll, {
  LoadFilesAllProps
} from '../loader/load-files/load-files-all';
import EventEmitter from '../event-emitter/event-emitter';

const Loading = () => <div>loading...</div>;

export class MicroFEShell extends PureComponent<ShellProps, ShellState> {
  log: (...args: any) => void;

  _isMounted = false;

  constructor(props) {
    super(props);

    this.log = log(this.props.name, this.props.debug);

    this.state = {
      shellLoading: true,
      LoadedComponents: {}
    };

    this.logTime();

    EventEmitter.on('updateStateLoadedComponents', ({ loadAllFiles }) =>
      this.updateStateLoadedComponents(loadAllFiles)
    );
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadShell();
  }

  componentDidUpdate(prevProps: ShellProps) {
    if (deepEqual(prevProps.config, this.props.config)) return null;
    this.log('component did update with changes');

    this.loadShell();

    // TODO: what do we return here??
    return {};
  }

  componentWillUnmount() {
    const { name } = this.props;

    [
      `${name}-shell`,
      `${name}-global`,
      `${name}-core-files`,
      `${name}-non-core-files`,
      'updateStateLoadedComponents'
    ].forEach(type => EventEmitter.off(type));

    this._isMounted = false;
  }

  updateStateLoadedComponents(loadedComponents) {
    if (this._isMounted)
      this.setState(({ LoadedComponents }) => ({
        LoadedComponents: {
          ...deepmerge(LoadedComponents, loadedComponents)
        }
      }));
  }

  updateStateLoading(loading) {
    if (this._isMounted) this.setState({ shellLoading: loading });
  }

  logTime() {
    const { debug, name } = this.props;
    [
      `${name}-shell`,
      `${name}-global`,
      `${name}-core-files`,
      `${name}-non-core-files`
    ].forEach(type => {
      EventEmitter.on(
        type,
        ({ msg, args, before, beforeFunc, after, afterFunc, time }) => {
          if (before) {
            if (beforeFunc && typeof beforeFunc === 'function') beforeFunc();

            if (debug && time) {
              console.log(`[${name}] ${type} STARTING`);
              console.time(`[${name}] ${type} loaded in`);
            }
          }

          if (msg && args) this.log(msg, args);
          else if (msg) this.log(msg);
          else if (args) this.log(args);

          if (after) {
            if (afterFunc && typeof afterFunc === 'function') afterFunc();

            if (debug && time) {
              console.timeEnd(`[${name}] ${type} loaded in`);
              console.log(`[${name}] ${type} ENDING`);
            }
          }
        }
      );
    });
  }

  async loadGlobals() {
    EventEmitter.emit(`${this.props.name}-global`, {
      msg: 'loading global with',
      args: {
        globals: this.props.config.globals
      },
      before: true,
      beforeFunc: this.props.beforeGlobalLoad,
      time: true
    });

    await loadGlobals(this.props, e =>
      EventEmitter.emit(`${this.props.name}-global`, { msg: e })
    );

    EventEmitter.emit(`${this.props.name}-global`, {
      after: true,
      afterFunc: this.props?.afterGlobalLoad,
      time: true
    });
  }

  async loadCoreFiles() {
    EventEmitter.emit(`${this.props.name}-core-files`, {
      before: true,
      beforeFunc: this.props.beforeCoreFilesLoad,
      time: true
    });

    const coreFiles = getFilteredFiles<ConfigurationManifestComponent>(
      this.props.config.files,
      x => x.core
    );

    EventEmitter.emit(`${this.props.name}-core-files`, {
      msg: 'loading the following core files',
      args: {
        coreFiles
      }
    });

    const loadAllFiles = await loadFilesAll(
      { ...this.props, files: coreFiles },
      (loadedComponents: LoadFilesAllProps) => {
        if (loadedComponents)
          EventEmitter.emit(`${this.props.name}-core-files`, {
            msg: 'loaded core file',
            args: loadedComponents
          });
      }
    );

    if (loadAllFiles) {
      this.updateStateLoadedComponents(loadAllFiles);
    }

    EventEmitter.emit(`${this.props.name}-core-files`, {
      after: true,
      afterFunc: this.props.afterCoreFilesLoad,
      time: true
    });

    return loadAllFiles;
  }

  async loadNonCoreFiles() {
    EventEmitter.emit(`${this.props.name}-non-core-files`, {
      before: true,
      beforeFunc: this.props.beforeNonCoreFilesLoad,
      time: true
    });

    const nonCoreFiles = getFilteredFiles<ConfigurationManifestComponent>(
      this.props.config.files,
      x => !x.core
    );

    EventEmitter.emit(`${this.props.name}-non-core-files`, {
      msg: 'loading the following non-core files',
      args: {
        nonCoreFiles
      }
    });

    this.log('starting non-core files', { nonCoreFiles });
    const loadStreamFiles = await loadFilesAll(
      { ...this.props, files: nonCoreFiles },
      (loadedComponents: LoadFilesAllProps) => {
        if (loadedComponents) {
          this.updateStateLoadedComponents(loadedComponents);
          EventEmitter.emit(`${this.props.name}-non-core-files`, {
            msg: 'loaded non core file',
            args: loadedComponents
          });
        }
      }
    );

    EventEmitter.emit(`${this.props.name}-non-core-files`, {
      after: true,
      afterFunc: this.props.afterNonCoreFilesLoad,
      time: true
    });

    return loadStreamFiles;
  }

  async loadShell() {
    EventEmitter.emit(`${this.props.name}-shell`, {
      msg: 'loading shell with props',
      args: {
        name: this.props?.name,
        env: this.props?.env,
        config: this.props?.config
      },
      before: true,
      beforeFunc: this.props?.beforeShellLoad,
      time: true
    });

    // check config to make sure its valid
    const { config, env } = this.props;
    if (!config || config?.files?.length < 1) {
      this.log(`unable to read config from ${env}`);
      // TODO: after shell func
      return null;
    }

    this.updateStateLoading(true);

    //* load global files
    await this.loadGlobals();

    //* load core files
    await this.loadCoreFiles();

    //* load non-core files
    await this.loadNonCoreFiles();

    this.updateStateLoading(false);

    EventEmitter.emit(`${this.props.name}-shell`, {
      after: true,
      afterFunc: this.props.afterShellLoad,
      time: true
    });

    return null; // ?do we need to return anything
  }

  render() {
    const { children, className, loading, ...rest } = this.props;
    const { shellLoading } = this.state;

    return (
      <ApplicationContext.Provider value={{ mfer: { ...this.state, ...rest } }}>
        <div id={`${rest.name}-shell`} className={className}>
          {loading && shellLoading ? (
            typeof loading === 'boolean' ? (
              <Loading />
            ) : (
              loading
            )
          ) : (
            children
          )}
        </div>
      </ApplicationContext.Provider>
    );
  }
}
